/**
 * masterDetailSplit — LUẬT của màn hình danh sách CHIA ĐÔI (master ở trên, chi tiết ở dưới).
 *
 * Ở chế độ bảng, chứng từ có lưới chi tiết nên xem được ngay chi tiết của dòng đang chọn mà không phải
 * mở form — đúng thói quen của InputBase cũ (`InputBaseDetail` gắn dưới lưới, bật/tắt bằng `hide_detail`).
 * Khác bản cũ ở chỗ: bản cũ cố định 200px, bản này chia đôi và kéo được thanh ngăn.
 *
 * File KHÔNG import gì để test node chạy được trên chính mã nguồn này.
 */

const DEFAULT_SPLIT_RATIO = 0.5;   // mặc định hai phần bằng nhau
const MIN_SPLIT_RATIO = 0.2;       // không cho bóp phần nào nhỏ tới mức vô dụng
const MAX_SPLIT_RATIO = 0.85;

/** Ép tỉ lệ về khoảng cho phép; giá trị hỏng (NaN/null/chuỗi) -> quay về mặc định. */
function clampSplitRatio(ratio, min = MIN_SPLIT_RATIO, max = MAX_SPLIT_RATIO) {
	// CHỈ nhận số, hoặc chuỗi số (giá trị đọc từ localStorage). Mọi thứ khác về mặc định.
	// Không dùng Number() trực tiếp: Number(null) và Number([]) đều ra 0 -> sẽ bị ép về MIN, tức là bảng
	// co lại còn 20% chỉ vì giá trị lưu bị rỗng/hỏng, mà đúng ra phải quay về chia đôi.
	if (typeof ratio !== 'number' && (typeof ratio !== 'string' || ratio.trim() === '')) return DEFAULT_SPLIT_RATIO;
	const n = typeof ratio === 'number' ? ratio : Number(ratio);
	if (!Number.isFinite(n)) return DEFAULT_SPLIT_RATIO;
	if (n < min) return min;
	if (n > max) return max;
	return n;
}

/** Vị trí con trỏ -> tỉ lệ phần TRÊN. `rect` là DOMRect của khung chứa cả hai phần. */
function ratioFromPointer(clientY, rect) {
	if (!rect || !rect.height) return DEFAULT_SPLIT_RATIO;
	return clampSplitRatio((clientY - rect.top) / rect.height);
}

/**
 * Field nào thành TAB chi tiết ở nửa dưới màn hình DANH SÁCH chia đôi: các lưới con (ObjectArray).
 *
 * KHÔNG xét `hideInView`. Cờ đó chỉ dành cho TRANG XEM (DataView), không áp cho danh sách. Chứng từ cũ
 * luôn khai `on_view: false` cho các field mảng (adaptListinfoFields dịch thành `hideInView: true`), nên
 * nếu lọc theo cờ này thì màn hình chứng từ mất sạch tab chi tiết.
 *
 * Chỉ loại lưới con NỘI BỘ: khai cứng ẩn ở CẢ danh sách LẪN form (`hideInList: true` và
 * `hideInForm: true`) — người dùng không nhập, cũng không xem trên lưới, vd `fb_dang` (các Trang đã đăng)
 * của nội dung marketing. Lưới chi tiết của chứng từ luôn nhập được trên form nên không bao giờ rơi vào
 * trường hợp này. Cờ dạng SCRIPT không đánh giá ở đây (cần context/dòng dữ liệu) nên không loại.
 */
function detailTabFields(fields) {
	return (fields || []).filter(f => f && f.field && f.type === 'ObjectArray'
		&& !(f.hideInList === true && f.hideInForm === true));
}

/**
 * Có chia đôi màn hình không.
 * Mặc định BẬT khi có lưới chi tiết; khai `showDetailOnGrid: false` để tắt cho màn hình không muốn.
 * Chỉ áp dụng ở chế độ bảng trên màn rộng — màn hẹp chia đôi thì cả hai phần đều không dùng được.
 */
function shouldShowDetailPane({ showDetailOnGrid, fields, viewMode, isMobile } = {}) {
	if (showDetailOnGrid === false) return false;
	if (viewMode !== 'table') return false;
	if (isMobile) return false;
	return detailTabFields(fields).length > 0;
}

/** Khoá của một dòng dữ liệu (_id hoặc id). */
function rowIdOf(row) {
	if (!row) return null;
	return row._id || row.id || null;
}

/**
 * Dòng nào đang được xem ở khung chi tiết.
 *  - Danh sách rỗng -> không có dòng nào.
 *  - Dòng đang chọn CÒN trong danh sách -> giữ nguyên (đừng nhảy về đầu khi người dùng cuộn tải thêm).
 *  - Còn lại (mới tải, vừa đổi bộ lọc, dòng cũ đã bị xoá) -> chọn DÒNG ĐẦU TIÊN.
 */
function resolveActiveRowId(items, currentId) {
	const list = Array.isArray(items) ? items : [];
	if (list.length === 0) return null;
	if (currentId && list.some(r => rowIdOf(r) === currentId)) return currentId;
	return rowIdOf(list[0]);
}

/** Khoá lưu tỉ lệ đã kéo — theo TỪNG màn hình, vì mỗi màn hình có số cột/số dòng khác nhau. */
function splitStorageKey(code) {
	return `datalist_split_${code || 'default'}`;
}

/** Đọc tỉ lệ đã lưu. Không có/hỏng/không đọc được storage -> mặc định. */
function readSplitRatio(storage, code) {
	try {
		const raw = storage && storage.getItem(splitStorageKey(code));
		if (raw === null || raw === undefined || raw === '') return DEFAULT_SPLIT_RATIO;
		return clampSplitRatio(raw);
	} catch (e) {
		return DEFAULT_SPLIT_RATIO;
	}
}

/** Ghi tỉ lệ. Storage hỏng (chế độ riêng tư, hết chỗ) thì im lặng bỏ qua — không được làm gãy giao diện. */
function writeSplitRatio(storage, code, ratio) {
	try {
		if (storage) storage.setItem(splitStorageKey(code), String(clampSplitRatio(ratio)));
		return true;
	} catch (e) {
		return false;
	}
}

export {
	DEFAULT_SPLIT_RATIO, MIN_SPLIT_RATIO, MAX_SPLIT_RATIO,
	clampSplitRatio, ratioFromPointer, detailTabFields, shouldShowDetailPane,
	rowIdOf, resolveActiveRowId,
	splitStorageKey, readSplitRatio, writeSplitRatio,
};
